import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Alert, CircularProgress, Typography } from '@mui/material';
import { api } from '../services/api';

interface DocumentDetailResponse {
  filename: string;
  contract_type: string;
  text_content: string;
  form_data?: Record<string, any> | null;
  generated_at?: string | null;
  file_size?: number | null;
  download_url?: string | null;
  download_url_pdf?: string | null;
  download_url_docx?: string | null;
}

interface ModifyContractResponse {
  modified_content: string;
  modification_summary: string;
  changes_made: string[];
}

interface SaveEditedResponse {
  success: boolean;
  filename: string;
  file_size: number;
  generated_at: string;
  download_url: string;
  download_url_pdf?: string | null;
  download_url_docx?: string | null;
  text_content: string;
}

interface Props {
  open: boolean;
  filename: string | null;
  onClose: () => void;
}

const DocumentDetailDialog: React.FC<Props> = ({ open, filename, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DocumentDetailResponse | null>(null);
  const [editedText, setEditedText] = useState('');
  const [prompt, setPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [modifying, setModifying] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!open || !filename) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/documents/detail/${encodeURIComponent(filename)}`);
        const data: DocumentDetailResponse = resp.data;
        setDetail(data);
        setEditedText(data.text_content || '');
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Nepodařilo se načíst detail dokumentu');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, filename]);

  const handleAIModify = async () => {
    if (!detail) return;
    setModifying(true);
    setError(null);
    try {
      const resp = await api.post<ModifyContractResponse>(`/documents/modify-contract`, {
        current_content: editedText,
        modification_request: prompt,
        contract_type: detail.contract_type,
        form_data: detail.form_data || null,
      });
      setEditedText(resp.data.modified_content);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'AI úprava selhala');
    } finally {
      setModifying(false);
    }
  };

  const handleSaveEdited = async () => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await api.post<SaveEditedResponse>(`/documents/save-edited`, {
        filename: detail.filename,
        modified_content: editedText,
        contract_type: detail.contract_type,
      });
      const { download_url } = resp.data;
      if (download_url) {
        window.open(download_url, '_blank');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Uložení upraveného dokumentu selhalo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Detail dokumentu</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" py={4}><CircularProgress /></Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : detail ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Soubor: {detail.filename} {detail.generated_at ? `• ${new Date(detail.generated_at).toLocaleString('cs-CZ')}` : ''}
            </Typography>
            <TextField
              label="Text smlouvy"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              fullWidth
              multiline
              minRows={14}
            />
            <TextField
              label="Doplnění / úprava (AI)"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Např.: Přidej ustanovení o předávacím protokolu a zárukách"
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        {(detail?.download_url_pdf || detail?.download_url) && (
          <Button 
            onClick={() => window.open((detail.download_url_pdf || detail.download_url)!, '_blank')} 
            color="inherit"
            variant="text"
            sx={{ color: '#1F1F1F' }}
          >
            Stáhnout PDF
          </Button>
        )}
        {detail?.download_url_docx && (
          <Button 
            onClick={() => window.open(detail.download_url_docx!, '_blank')} 
            color="inherit"
            variant="text"
            sx={{ color: '#1F1F1F' }}
          >
            Stáhnout DOCX
          </Button>
        )}
        <Button 
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            color: '#212121',
            borderColor: '#BDBDBD',
            backgroundColor: '#FAFAFA',
            '&:hover': { backgroundColor: '#F0F0F0', borderColor: '#9E9E9E' }
          }}
        >
          Zavřít
        </Button>
        <Button 
          onClick={handleAIModify} 
          disabled={modifying || !detail} 
          variant="contained" 
          color="secondary"
          sx={{ fontWeight: 600 }}
        >
          {modifying ? 'Upravuji…' : 'AI doplnit'}
        </Button>
        <Button 
          onClick={handleSaveEdited} 
          disabled={saving || !detail} 
          variant="contained" 
          color="primary"
        >
          {saving ? 'Ukládám…' : 'Uložit a stáhnout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDetailDialog;
